import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { ChevronRight } from '../../../components/ChevronRight'
import { GameTitle } from '../../../components/GameTitle'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import {
  dishDisplayName, fetchWeeklyWinners, archiveDateLabel,
} from '../../../lib/api/weekly'
import type { WeeklyWinnerRow } from '../../../lib/database.types'
import { readError } from '../../../lib/errors'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onViewWeek: (eventId: string) => void
}

/**
 * Flat chronological list of finalized IET weeks — one row per week,
 * tap to read that week's stories. No medals, no gold dressing,
 * just a scannable archive.
 */
export function IETArchiveScreen({ onBack, onViewWeek }: Props) {
  const t = useT()
  const [winners, setWinners] = useState<WeeklyWinnerRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // No week is excluded. A week that handed out a trophy must be
    // listed here, otherwise tapping that trophy opens a week the
    // archive says doesn't exist. Weeks still being voted on are
    // already kept out by the finalized check inside the RPC, which
    // is the correct gate; excluding `lastWeek` on top of that also
    // hid the most recently settled week along with it.
    fetchWeeklyWinners()
      .then(rows => { if (!cancelled) setWinners(rows) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="screen">
      <BackgroundTitle text={t("I'd Eat That Archive")} color="#B58615" />
      <PageHeader title={t("I'd Eat That · Archive")} onBack={onBack} />

      <div className="flex-1 px-5 py-4 space-y-2 overflow-y-auto pb-6 relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <img src="/games/ideatthat.webp" alt="I'd Eat That"
               className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0" />
          <div>
            <GameTitle game="ideatthat" size="sm" />
            <p className="text-xs text-brand-muted">
              {t("Read every past week's winning food story")}
            </p>
          </div>
        </div>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {winners === null && (
          <>
            <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {winners?.length === 0 && (
          <p className="text-sm text-brand-muted text-center py-8">
            {t('No finalised weeks yet.')}
          </p>
        )}

        {winners && winners.map(w => (
          <button
            key={w.event_id}
            onClick={() => onViewWeek(w.event_id)}
            className="w-full iet-gold border-2 border-game-iet-dark
                       rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text truncate">
                {dishDisplayName(w, { display_name: w.author_name })}
              </p>
              <p className="text-xs text-brand-muted truncate">
                {archiveDateLabel(w.week_start)} · by {w.author_name}
              </p>
            </div>
            <ChevronRight color="#B58615" />
          </button>
        ))}
      </div>
    </div>
  )
}
